import React, { useState } from 'react';
import { X, Shield, ExternalLink, Check } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import api from '../utils/api';

interface KVKKConsentModalProps {
  isOpen: boolean;
  onClose: () => void;
  onAccept: () => void;
  consentType: 'login' | 'register' | 'data_processing';
  title?: string;
  description?: string;
}

const KVKKConsentModal: React.FC<KVKKConsentModalProps> = ({
  isOpen,
  onClose,
  onAccept,
  consentType,
  title,
  description
}) => {
  const { t } = useTranslation();
  const [consentGiven, setConsentGiven] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const getConsentContent = () => {
    switch (consentType) {
      case 'login':
        return {
          title: title || t('kvkkConsent.login.title'),
          description: description || t('kvkkConsent.login.description'),
          points: t('kvkkConsent.login.points', { returnObjects: true }) as string[]
        };
      case 'register':
        return {
          title: title || t('kvkkConsent.register.title'),
          description: description || t('kvkkConsent.register.description'),
          points: t('kvkkConsent.register.points', { returnObjects: true }) as string[]
        };
      case 'data_processing':
        return {
          title: title || t('kvkkConsent.dataProcessing.title'),
          description: description || t('kvkkConsent.dataProcessing.description'),
          points: t('kvkkConsent.dataProcessing.points', { returnObjects: true }) as string[]
        };
      default:
        return {
          title: title || t('kvkkConsent.default.title'),
          description: description || t('kvkkConsent.default.description'),
          points: t('kvkkConsent.default.points', { returnObjects: true }) as string[]
        };
    }
  };

  const content = getConsentContent();

  const handleAccept = async () => {
    if (!consentGiven) {
      setError(t('kvkkConsent.required'));
      return;
    }

    setLoading(true);
    setError(null);

    try {
      // KVKK onamını backend'e gönder
      await api.post('/api/kvkk/consent', {
        consent_type: consentType,
        consent_metadata: {
          source: 'modal',
          timestamp: new Date().toISOString()
        }
      });

      onAccept();
    } catch (err: any) {
      setError(t('kvkkConsent.saveError'));
      console.error('KVKK consent error:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleReject = () => {
    onClose();
  };

  const handleLinkClick = (e: React.MouseEvent) => {
    e.preventDefault();
    window.open('/legal/kvkk-aydinlatma', '_blank');
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full p-6 max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <Shield className="text-lotus-gold" size={24} />
            <h3 className="text-xl font-semibold text-gray-900">
              {content.title}
            </h3>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <X size={20} />
          </button>
        </div>

        {/* Description */}
        <p className="text-gray-600 mb-6 leading-relaxed">
          {content.description}
        </p>

        {/* Consent Points */}
        <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 mb-6">
          <h4 className="font-medium text-gray-900 mb-3">{t('kvkkConsent.importantInfo')}</h4>
          <ul className="space-y-2">
            {content.points.map((point, index) => (
              <li key={index} className="flex items-start gap-2">
                <Check className="text-green-500 mt-0.5 flex-shrink-0" size={16} />
                <span className="text-sm text-gray-700">{point}</span>
              </li>
            ))}
          </ul>
        </div>

        {/* KVKK Link */}
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
          <div className="flex items-center gap-2 mb-2">
            <ExternalLink className="text-blue-600" size={16} />
            <span className="text-sm font-medium text-blue-900">
              {t('kvkkConsent.detailsTitle')}
            </span>
          </div>
          <p className="text-sm text-gray-700 mb-3">
            {t('kvkkConsent.detailsDescription')}
          </p>
          <a
            href="/legal/kvkk-aydinlatma"
            target="_blank"
            rel="noopener noreferrer"
            onClick={handleLinkClick}
            className="inline-flex items-center gap-2 text-sm text-blue-600 hover:text-blue-800 underline"
          >
            {t('kvkkConsent.readKvkk')}
            <ExternalLink size={14} />
          </a>
        </div>

        {/* Consent Checkbox */}
        <div className="mb-6">
          <label className="flex items-start gap-3 cursor-pointer">
            <input
              type="checkbox"
              id="kvkk-consent-checkbox"
              checked={consentGiven}
              onChange={(e) => setConsentGiven(e.target.checked)}
              className="w-4 h-4 text-lotus-gold rounded mt-1"
            />
            <label htmlFor="kvkk-consent-checkbox" className="text-sm text-gray-700 leading-relaxed">
              {t('kvkkConsent.checkboxPrefix')}
              <a 
                href="/legal/kvkk-aydinlatma" 
                target="_blank" 
                rel="noopener noreferrer"
                className="text-lotus-gold underline hover:text-lotus-gold/80"
              >
                {t('kvkkConsent.checkboxLink')}
              </a>
              {t('kvkkConsent.checkboxSuffix')}
            </label>
          </label>
        </div>

        {/* Error Message */}
        {error && (
          <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-red-600 text-sm">
            {error}
          </div>
        )}

        {/* Action Buttons */}
        <div className="flex gap-3">
          <button
            onClick={handleAccept}
            disabled={!consentGiven || loading}
            className="flex-1 bg-lotus-gold text-white px-6 py-3 rounded-lg hover:bg-lotus-gold/90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed font-medium"
          >
            {loading ? t('kvkkConsent.processing') : t('kvkkConsent.accept')}
          </button>
          <button
            onClick={handleReject}
            disabled={loading}
            className="flex-1 bg-gray-200 text-gray-700 px-6 py-3 rounded-lg hover:bg-gray-300 transition-colors disabled:opacity-50 font-medium"
          >
            {t('kvkkConsent.cancel')}
          </button>
        </div>

        {/* Footer Info */}
        <div className="mt-4 pt-4 border-t border-gray-200">
          <p className="text-xs text-gray-500 text-center">
            {t('kvkkConsent.footer')}
          </p>
        </div>
      </div>
    </div>
  );
};

export default KVKKConsentModal;
